import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Headset, 
  BarChart2, 
  FileText, 
  Link as LinkIcon, 
  Mail, 
  Building, 
  Users 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { useMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

type CaseStudy = {
  sector: string;
  client?: string;
  challenge: string;
  approaches: string[];
  result: string;
  icon: JSX.Element;
};

const caseStudies: CaseStudy[] = [
  {
    sector: "Facilitaire diensten",
    client: "Globale marktleider",
    challenge: "Geen structuur in prospectie, wel groeipotentieel",
    approaches: [
      "Koud bellend team + Podio CRM-flows", 
      "Wekelijkse reporting & targeting op regio's"
    ],
    result: "+38 afspraken per maand, 12 nieuwe klanten in 6 maanden",
    icon: <Building className="h-10 w-10 text-brand-blue" />
  },
  {
    sector: "HR & interim",
    client: "Anonieme top 5 speler",
    challenge: "Tekort aan warme leads voor consultants",
    approaches: [
      "Interne opleiding van medewerkers", 
      "Inkomende leads opgevolgd via Contract Engine™"
    ],
    result: "+19% conversie & -50% tijdverlies op offertes",
    icon: <Users className="h-10 w-10 text-brand-blue" />
  },
  {
    sector: "SaaS",
    client: "Groeibedrijf Antwerpen",
    challenge: "Moeilijke toegang tot enterprise klanten",
    approaches: [
      "Hybride outreach met telefoon en WhatsApp", 
      "Langetermijnnurturing via email campaigns"
    ],
    result: "4 enterprise deals van +€50k in eerste kwartaal",
    icon: <FileText className="h-10 w-10 text-brand-blue" />
  },
  {
    sector: "Telecom",
    client: "Belgische marktleider",
    challenge: "Te veel leads, te weinig opvolging",
    approaches: [
      "LeadSnap App voor snelle kwalificatie", 
      "Follow-up algoritme voor optimale timing"
    ],
    result: "Reactietijd verlaagd van 48 uur naar 3 uur, conversie +14%",
    icon: <Headset className="h-10 w-10 text-brand-blue" />
  },
  {
    sector: "Digitaal marketing",
    client: "Fast-growing agency",
    challenge: "Inconsistente instroom nieuwe klanten",
    approaches: [
      "Geautomatiseerde prospectieflow", 
      "Datagedreven targeting op groeisectoren"
    ],
    result: "Stabiele instroom van 8-10 nieuwe projecten per maand",
    icon: <BarChart2 className="h-10 w-10 text-brand-blue" />
  },
  {
    sector: "Verzekeringen",
    client: "Regionale makelaarsgroep",
    challenge: "Moeite met prospectie in competitieve markt",
    approaches: [
      "Niche-targeting via databronnen", 
      "Script-optimalisatie voor makelaars"
    ],
    result: "+22% meer meetings met beslissers, -15% acquisitiekost",
    icon: <LinkIcon className="h-10 w-10 text-brand-blue" />
  },
];

const CaseStudyCard = ({ study }: { study: CaseStudy }) => {
  return (
    <Card className="overflow-hidden h-full hover:shadow-md transition-shadow duration-300">
      <CardContent className="p-0">
        <div className="p-6 flex flex-col h-full">
          <div className="flex items-start justify-between mb-4">
            <Badge className="bg-brand-lightBlue text-brand-blue border-0 mb-2">
              {study.sector}
            </Badge>
            <div className="bg-brand-lightBlue/30 p-2 rounded-full">
              {study.icon}
            </div>
          </div>
          
          {study.client && (
            <h4 className="font-semibold text-lg mb-2 text-gray-900">{study.client}</h4>
          )}
          
          <div className="space-y-4 flex-grow">
            <div>
              <p className="text-sm text-gray-500 mb-1">Uitdaging:</p>
              <p className="text-gray-700">{study.challenge}</p>
            </div>
            
            <div>
              <p className="text-sm text-gray-500 mb-1">Onze aanpak:</p>
              <ul className="list-disc pl-5 text-gray-700 space-y-1">
                {study.approaches.map((approach, idx) => (
                  <li key={idx}>{approach}</li>
                ))}
              </ul>
            </div>
            
            <div className="mt-auto">
              <p className="text-sm text-gray-500 mb-1">Resultaat:</p>
              <p className="font-medium text-brand-blue">{study.result}</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ImpactSection = () => {
  const isMobile = useMobile();
  const [selectedSector, setSelectedSector] = useState<string | null>(null);
  const [filteredCaseStudies, setFilteredCaseStudies] = useState<CaseStudy[]>(caseStudies);
  const sectionRef = useRef<HTMLElement>(null);
  const location = useLocation();
  
  // Extract unique sectors for filter buttons
  const sectors = Array.from(new Set(caseStudies.map(study => study.sector)));
  
  // Filter case studies when selected sector changes
  useEffect(() => {
    if (selectedSector) {
      setFilteredCaseStudies(caseStudies.filter(study => study.sector === selectedSector));
    } else {
      setFilteredCaseStudies(caseStudies);
    }
  }, [selectedSector]);

  // Scroll handling for both location state and hash
  useEffect(() => {
    // Check if this section should be scrolled to (either from hash or from state)
    const shouldScrollToThisSection = location.hash === '#impact' || 
                                      location.state?.scrollToSection === 'impact';
    
    if (shouldScrollToThisSection && sectionRef.current) {
      // Use requestAnimationFrame to ensure the browser has painted before scrolling
      requestAnimationFrame(() => {
        // Try the most reliable method first
        const yOffset = -80; // Adjust based on header height
        const y = sectionRef.current!.getBoundingClientRect().top + window.pageYOffset + yOffset;
        
        // Attempt scroll at different delays to maximize chances of success
        setTimeout(() => window.scrollTo({ top: y, behavior: 'smooth' }), 100);
        setTimeout(() => {
          // Fallback to scrollIntoView if the first method doesn't work
          sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 300);
      });
    }
  }, [location]); // Re-run when location changes

  return (
    <section className="section bg-brand-gray py-16" id="impact" ref={sectionRef}>
      <div className="container">
        <div className="text-center mb-8">
          <h2 className="section-title">Onze impact bij Vlaamse groeibedrijven</h2>
          <p className="section-subtitle mx-auto">
            Deze ondernemers kozen voor Afsprakenmaker en plukken vandaag nog de vruchten.
          </p>
        </div>
        
        {/* Filter buttons */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          <Button
            variant={selectedSector === null ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedSector(null)}
            className="rounded-full"
          >
            Alle sectoren
          </Button>
          {sectors.map((sector) => (
            <Button
              key={sector}
              variant={selectedSector === sector ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedSector(sector)}
              className="rounded-full"
            >
              {sector}
            </Button>
          ))}
        </div>
        
        <div className={cn("transition-all duration-300", 
          filteredCaseStudies.length === 0 ? "opacity-50" : "opacity-100"
        )}>
          {filteredCaseStudies.length === 0 && (
            <p className="text-center text-gray-500 my-12">
              Geen resultaten gevonden voor deze sector.
            </p>
          )}
          
          {isMobile ? (
            <Carousel className="w-full">
              <CarouselContent>
                {filteredCaseStudies.map((study, index) => (
                  <CarouselItem key={index} className="md:basis-1/2 lg:basis-1/3">
                    <div className="p-1">
                      <CaseStudyCard study={study} />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <div className="flex justify-center mt-6 gap-2">
                <CarouselPrevious className="relative inset-0 translate-x-0 translate-y-0 bg-white hover:bg-gray-50" />
                <CarouselNext className="relative inset-0 translate-x-0 translate-y-0 bg-white hover:bg-gray-50" />
              </div>
            </Carousel>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in">
              {filteredCaseStudies.map((study, index) => (
                <CaseStudyCard key={index} study={study} />
              ))}
            </div>
          )}
        </div>
        
        <div className="mt-12 text-center">
          <Link to="/diensten" className="bg-brand-blue hover:bg-brand-darkBlue text-white py-3 px-6 rounded-md font-medium inline-flex items-center gap-2 transition-colors">
            Ontdek wat wij voor u kunnen doen
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ImpactSection;
